import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { notification, message } from "antd";
import axios from "axios";
import { mergeMensajes } from "../../../../../Utils/whatsapp.utils";

/**
 * Lógica compartida del chat de WhatsApp (lista de conversaciones, hilo de mensajes,
 * envío, paginación y eventos en tiempo real). La usan tanto la página completa
 * (BandejaWhatsapp) como la burbuja global (BurbujaWhatsappGlobal).
 *
 * @param {{ headersToken: object }} opts
 */
const useChatWhatsapp = ({ headersToken }) => {
  const [conexionActiva, setConexionActiva] = useState(null);
  const [conversaciones, setConversaciones] = useState([]);
  const [conversacionActiva, setConversacionActiva] = useState(null);
  const [mensajes, setMensajes] = useState([]);
  const [loadingConversaciones, setLoadingConversaciones] = useState(true);
  const [loadingMensajes, setLoadingMensajes] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [esMaster, setEsMaster] = useState(false);
  const [hayMas, setHayMas] = useState(false);
  const [cargandoMas, setCargandoMas] = useState(false);
  const [modoLineas, setModoLineas] = useState({
    numeroPorUsuario: false,
    puedeVerOtros: false,
    puedeDerivar: false,
    miLineaId: null,
    miNumero: null,
    lineaPrincipalId: null,
    numeroPrincipal: null,
  });

  const headers = useMemo(() => ({ headers: headersToken }), [headersToken]);

  const conversacionActivaRef = useRef(null);
  useEffect(() => {
    conversacionActivaRef.current = conversacionActiva;
  }, [conversacionActiva]);

  const mensajesRef = useRef([]);
  useEffect(() => {
    mensajesRef.current = mensajes;
  }, [mensajes]);

  const cargarEstadoConexion = useCallback(() => {
    axios
      .post("whatsapp/estadoConexion", {}, headers)
      .then((r) => {
        if (r.data.success === 2) {
          setConexionActiva(!!r.data.conexion?.activo);
        }
      })
      .catch(() => setConexionActiva(false));
  }, [headers]);

  const cargarConversaciones = useCallback(() => {
    setLoadingConversaciones(true);
    axios
      .post("whatsapp/listarConversaciones", {}, headers)
      .then((r) => {
        if (r.data.success === 2) {
          setConversaciones(r.data.conversaciones || []);
          setEsMaster(!!r.data.esMaster);
          setModoLineas({
            numeroPorUsuario: !!r.data.numeroPorUsuario,
            puedeVerOtros: !!r.data.puedeVerOtros,
            puedeDerivar: !!r.data.puedeDerivar,
            miLineaId: r.data.miLineaId || null,
            miNumero: r.data.miNumero || null,
            lineaPrincipalId: r.data.lineaPrincipalId || null,
            numeroPrincipal: r.data.numeroPrincipal || null,
          });
        }
      })
      .finally(() => setLoadingConversaciones(false));
  }, [headers]);

  const cargarMensajes = useCallback(
    (conversacionId) => {
      setLoadingMensajes(true);
      axios
        .post("whatsapp/obtenerMensajes", { conversacionId }, headers)
        .then((r) => {
          if (r.data.success === 2) {
            setMensajes(r.data.mensajes || []);
            setHayMas(!!r.data.hayMas);
          }
        })
        .finally(() => setLoadingMensajes(false));
    },
    [headers],
  );

  const seleccionarConversacion = useCallback(
    (conv) => {
      setConversacionActiva(conv);
      cargarMensajes(conv._id);
      setConversaciones((prev) =>
        prev.map((c) => (c._id === conv._id ? { ...c, noLeidos: 0 } : c)),
      );
    },
    [cargarMensajes],
  );

  const volverALista = useCallback(() => {
    setConversacionActiva(null);
    setMensajes([]);
    setHayMas(false);
  }, []);

  const cargarMasMensajes = useCallback(() => {
    const conv = conversacionActivaRef.current;
    const before = mensajesRef.current[0]?._id;
    if (!conv || cargandoMas || !hayMas || !before) return;

    setCargandoMas(true);
    axios
      .post(
        "whatsapp/obtenerMensajes",
        { conversacionId: conv._id, before },
        headers,
      )
      .then((r) => {
        if (r.data.success === 2) {
          setMensajes((prev) => mergeMensajes(r.data.mensajes || [], prev));
          setHayMas(!!r.data.hayMas);
        }
      })
      .finally(() => setCargandoMas(false));
  }, [headers, cargandoMas, hayMas]);

  // Refresca la última página y la fusiona sin perder lo anterior.
  const refrescarUltimos = useCallback(
    (conversacionId) => {
      axios
        .post("whatsapp/obtenerMensajes", { conversacionId }, headers)
        .then((r) => {
          if (r.data.success === 2) {
            setMensajes((prev) => mergeMensajes(prev, r.data.mensajes || []));
          }
        });
    },
    [headers],
  );

  const procesarRespuestaEnvio = useCallback(
    (r) => {
      if (r.data.success === 2) {
        const nuevos = Array.isArray(r.data.mensajes)
          ? r.data.mensajes
          : [r.data.mensaje];
        setMensajes((prev) => [...prev, ...nuevos]);
        cargarConversaciones();
      } else if (r.data.success === 3) {
        notification.warning({
          message: "Fuera de la ventana de 24h",
          description:
            "Para enviar necesitas una plantilla aprobada (próximamente).",
        });
      } else {
        notification.error({ message: r.data.message || "No se pudo enviar." });
      }
    },
    [cargarConversaciones],
  );

  const enviarMensaje = useCallback(
    (texto) => {
      const conv = conversacionActivaRef.current;
      if (!conv || !texto?.trim()) return;

      setEnviando(true);
      axios
        .post(
          "whatsapp/enviarMensaje",
          { conversacionId: conv._id, texto },
          headers,
        )
        .then((r) => {
          if (r.data.success === 2) {
            setMensajes((prev) => [...prev, r.data.mensaje]);
            cargarConversaciones();
          } else if (r.data.success === 3) {
            notification.warning({
              message: "Fuera de la ventana de 24h",
              description:
                "Para escribir necesitas una plantilla aprobada (próximamente).",
            });
          } else {
            notification.error({
              message: r.data.message || "Error al enviar el mensaje.",
            });
          }
        })
        .catch(() => notification.error({ message: "Error de red al enviar." }))
        .finally(() => setEnviando(false));
    },
    [headers, cargarConversaciones],
  );

  const enviarMediaArchivo = useCallback(
    (formData) => {
      setEnviando(true);
      const headersMulti = { ...headersToken };
      delete headersMulti["Content-Type"];
      axios
        .post("whatsapp/enviarMedia", formData, { headers: headersMulti })
        .then((r) => procesarRespuestaEnvio(r))
        .catch(() => notification.error({ message: "Error de red al enviar." }))
        .finally(() => setEnviando(false));
    },
    [headersToken, procesarRespuestaEnvio],
  );

  const enviarPlantilla = useCallback(
    (formData) => {
      setEnviando(true);
      const headersMulti = { ...headersToken };
      delete headersMulti["Content-Type"];
      axios
        .post("whatsapp/enviarPlantilla", formData, { headers: headersMulti })
        .then((r) => procesarRespuestaEnvio(r))
        .catch(() => notification.error({ message: "Error de red al enviar." }))
        .finally(() => setEnviando(false));
    },
    [headersToken, procesarRespuestaEnvio],
  );

  const enviarServicio = useCallback(
    (payload) => {
      setEnviando(true);

      // Insumo con imagen personalizada → multipart (incluye el archivo).
      if (payload.tipo === "insumo" && payload.imagenArchivo) {
        const fd = new FormData();
        fd.append("conversacionId", payload.conversacionId);
        fd.append("insumoId", payload.insumoId);
        fd.append("texto", payload.texto || "");
        fd.append("incluirImagen", payload.incluirImagen ? "true" : "false");
        fd.append("guardarEnInsumo", payload.guardarEnInsumo ? "true" : "false");
        fd.append("fichas", JSON.stringify(payload.fichas || []));
        fd.append("imagen", payload.imagenArchivo);
        const headersMulti = { ...headersToken };
        delete headersMulti["Content-Type"];
        axios
          .post("whatsapp/enviarInsumo", fd, { headers: headersMulti })
          .then((r) => procesarRespuestaEnvio(r))
          .catch(() =>
            notification.error({ message: "Error de red al enviar." }),
          )
          .finally(() => setEnviando(false));
        return;
      }

      // "Detalle de insumo" puede generar varios mensajes; "interactivo" va a su
      // propio endpoint; el resto (ubicación, etc.) a enviarServicio.
      let endpoint = "whatsapp/enviarServicio";
      let body = payload;
      if (payload.tipo === "insumo") {
        endpoint = "whatsapp/enviarInsumo";
      } else if (payload.tipo === "interactivo") {
        endpoint = "whatsapp/enviarInteractivo";
        body = {
          conversacionId: payload.conversacionId,
          tipo: payload.tipoInteractivo,
          payload: payload.payload,
        };
      }
      axios
        .post(endpoint, body, headers)
        .then((r) => procesarRespuestaEnvio(r))
        .catch(() => notification.error({ message: "Error de red al enviar." }))
        .finally(() => setEnviando(false));
    },
    [headers, headersToken, procesarRespuestaEnvio],
  );

  const iniciarYEnviarPlantilla = useCallback(
    async (telefono, formData, onSuccessConv, contactoId) => {
      setEnviando(true);
      try {
        const r1 = await axios.post(
          "whatsapp/iniciarConversacion",
          { telefono, ...(contactoId ? { contactoId } : {}) },
          headers,
        );
        if (r1.data.success !== 2) {
          notification.error({ message: r1.data.message || "No se pudo iniciar la conversación." });
          return;
        }
        const conv = r1.data.conversacion;
        formData.append("conversacionId", conv._id);
        const headersMulti = { ...headersToken };
        delete headersMulti["Content-Type"];
        const r2 = await axios.post("whatsapp/enviarPlantilla", formData, { headers: headersMulti });
        if (r2.data.success === 2) {
          cargarConversaciones();
          onSuccessConv?.(conv);
        } else {
          notification.error({ message: r2.data.message || "No se pudo enviar la plantilla." });
        }
      } catch {
        notification.error({ message: "Error de red al iniciar la conversación." });
      } finally {
        setEnviando(false);
      }
    },
    [headers, headersToken, cargarConversaciones],
  );

  // Envía una plantilla a múltiples teléfonos 
  const enviarPlantillaMasiva = useCallback(
    async (formData, onResultado) => {
      setEnviando(true);
      try {
        const headersMulti = { ...headersToken };
        delete headersMulti["Content-Type"];
        const r = await axios.post("whatsapp/plantillaMasiva", formData, { headers: headersMulti });
        if (r.data.success === 2) {
          onResultado?.({ total: r.data.total, enviados: r.data.enviados, fallidos: r.data.fallidos || [] });
        } else {
          notification.error({ message: r.data.message || "Error en envío masivo." });
        }
      } catch {
        notification.error({ message: "Error de red en envío masivo." });
      } finally {
        setEnviando(false);
      }
    },
    [headersToken],
  );

  // Liga una conversación existente a un contacto del CRM y actualiza nombre en header/lista.
  const vincularContacto = useCallback(
    (conversacionId, contactoId, onOk) => {
      axios
        .post("whatsapp/vincularContacto", { conversacionId, contactoId }, headers)
        .then((r) => {
          if (r.data.success === 2) {
            const conv = r.data.conversacion;
            setConversacionActiva((prev) =>
              prev && String(prev._id) === String(conv._id)
                ? { ...prev, contactoId: conv.contactoId }
                : prev,
            );
            cargarConversaciones();
            message.success("Contacto vinculado a la conversación.");
            onOk?.(conv);
          } else {
            notification.error({
              message: r.data.message || "No se pudo vincular el contacto.",
            });
          }
        })
        .catch(() =>
          notification.error({ message: "Error de red al vincular el contacto." }),
        );
    },
    [headers, cargarConversaciones],
  );

  const derivarConversacion = useCallback(
    (conversacionId, usuarioId, mensaje, onOk) => {
      axios
        .post("whatsapp/derivarConversacion", { conversacionId, usuarioId, mensaje }, headers)
        .then((r) => {
          if (r.data.success === 2) {
            message.success(r.data.message || "Chat pasado.");
            setConversacionActiva((prev) =>
              prev && String(prev._id) === String(conversacionId) ? null : prev,
            );
            setMensajes([]);
            cargarConversaciones();
            onOk?.();
          } else {
            notification.error({ message: r.data.message || "No se pudo pasar el chat." });
          }
        })
        .catch(() => notification.error({ message: "Error de red al pasar el chat." }));
    },
    [headers, cargarConversaciones],
  );

  // Carga inicial.
  useEffect(() => {
    cargarEstadoConexion();
    cargarConversaciones();
  }, [cargarEstadoConexion, cargarConversaciones]);

  useEffect(() => {
    const handleWhatsappEvent = (e) => {
      const data = e.detail || {};
      if (data.tipo === "mensaje") {
        cargarConversaciones();
        const activa = conversacionActivaRef.current;
        if (activa && String(data.conversacionId) === String(activa._id)) {
          refrescarUltimos(activa._id);
        }
      } else if (data.tipo === "derivacion") {
        cargarConversaciones();
      } else if (data.tipo === "estado") {
        setMensajes((prev) =>
          prev.map((m) =>
            m.waMessageId === data.waMessageId
              ? { ...m, estado: data.estado }
              : m,
          ),
        );
      }
    };
    window.addEventListener("whatsapp_event", handleWhatsappEvent);
    return () =>
      window.removeEventListener("whatsapp_event", handleWhatsappEvent);
  }, [cargarConversaciones, refrescarUltimos]);

  const totalNoLeidos = useMemo(
    () => conversaciones.reduce((s, c) => s + (c.noLeidos || 0), 0),
    [conversaciones],
  );

  return {
    conexionActiva,
    conversaciones,
    conversacionActiva,
    mensajes,
    loadingConversaciones,
    loadingMensajes,
    enviando,
    esMaster,
    hayMas,
    cargandoMas,
    totalNoLeidos,
    ...modoLineas,
    // acciones
    cargarEstadoConexion,
    cargarConversaciones,
    seleccionarConversacion,
    volverALista,
    cargarMasMensajes,
    refrescarUltimos,
    enviarMensaje,
    enviarMediaArchivo,
    enviarPlantilla,
    enviarServicio,
    iniciarYEnviarPlantilla,
    enviarPlantillaMasiva,
    vincularContacto,
    derivarConversacion,
  };
};

export default useChatWhatsapp;
